import React from 'react';
import Summary from '@/components/Summary';
import { ArrowUp } from 'lucide-react';

interface RiskData {
  name: string;
  volatility: string;
  maxDrawdown: string;
  sharpeRatio: string;
  beta: string;
  volatilityColor: string;
  maxDrawdownColor: string;
}

// interface RiskAssessmentComparisonProps {
//   selected?: boolean;
//   onSelectChange?: (checked: boolean) => void;
// }

const RiskAssessmentComparison: React.FC<any> = ({
  selected = false,
  onSelectChange,
  totalData
}) => {
  const productInfo = totalData?.product_info || [];
  const product1Data = productInfo[0] || {};
  const product2Data = productInfo[1] || {};
  // 模拟数据
  const product1: RiskData = {
    name: product1Data.产品名称,
    volatility: '+28.5%',
    maxDrawdown: '-32.8%',
    sharpeRatio: '0.42',
    beta: '1.25',
    volatilityColor: 'text-red-600',
    maxDrawdownColor: 'text-green-600'
  };

  const product2: RiskData = {
    name: product2Data.产品名称,
    volatility: '+18.7%',
    maxDrawdown: '-24.6%',
    sharpeRatio: '0.78',
    beta: '0.92',
    volatilityColor: 'text-red-600',
    maxDrawdownColor: 'text-green-600'
  };

  const riskFields = [
    { key: 'name', label: '产品' },
    { key: 'volatility', label: '年化波动率' },
    { key: 'maxDrawdown', label: '最大回撤' },
    { key: 'sharpeRatio', label: '夏普比率' },
    { key: 'beta', label: '贝塔系数' }
  ];

  return (
    <div className="mb-3 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3">
      {/* 标题行 */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selected}
            onChange={e => onSelectChange?.(e.target.checked)}
            className="h-4 w-4 rounded border border-gray-300"
          />
          <span className="text-base font-semibold text-gray-900">风险评估对比</span>
        </div>
      </div>

      {/* 风险指标对比表格 */}
      <div className="flex overflow-hidden rounded-lg border border-gray-200">
        {/* 字段列 */}
        <div className="flex w-[93px] flex-col bg-gray-50">
          <div className="rounded-tl-lg bg-gray-100 px-2 py-1">
            <span className="text-xs font-semibold text-gray-900">产品</span>
          </div>
          {riskFields.slice(1).map(field => (
            <div key={field.key} className="border-t border-gray-200 px-2 py-1">
              <span className="text-xs text-gray-900">{field.label}</span>
            </div>
          ))}
        </div>

        {/* 产品1列 */}
        <div className="flex min-w-0 flex-1 flex-col bg-blue-50/50">
          <div className="truncate rounded-tr-lg bg-blue-100/10 px-2 py-1">
            <span className="text-xs font-semibold text-blue-600" title={product1.name}>
              {product1.name}
            </span>
          </div>
          <div className="border-t border-gray-200 px-2 py-1">
            <span className={`text-xs font-semibold ${product1.volatilityColor}`}>
              {product1.volatility}
            </span>
          </div>
          <div className="border-t border-gray-200 px-2 py-1">
            <span className={`text-xs font-semibold ${product1.maxDrawdownColor}`}>
              {product1.maxDrawdown}
            </span>
          </div>
          <div className="border-t border-gray-200 px-2 py-1">
            <span className="text-xs text-gray-900">{product1.sharpeRatio}</span>
          </div>
          <div className="border-t border-gray-200 px-2 py-1">
            <span className="text-xs text-gray-900">{product1.beta}</span>
          </div>
        </div>

        {/* 产品2列 */}
        <div className="flex min-w-0 flex-1 flex-col bg-orange-50/60">
          <div className="truncate rounded-tr-lg bg-orange-100/10 px-2 py-1">
            <span className="text-xs font-semibold text-orange-600" title={product2.name}>
              {product2.name}
            </span>
          </div>
          <div className="border-t border-gray-200 px-2 py-1">
            <span className={`text-xs font-semibold ${product2.volatilityColor}`}>
              {product2.volatility}
            </span>
          </div>
          <div className="border-t border-gray-200 px-2 py-1">
            <span className={`text-xs font-semibold ${product2.maxDrawdownColor}`}>
              {product2.maxDrawdown}
            </span>
          </div>
          <div className="border-t border-gray-200 px-2 py-1">
            <span className="text-xs text-gray-900">{product2.sharpeRatio}</span>
          </div>
          <div className="border-t border-gray-200 px-2 py-1">
            <span className="text-xs text-gray-900">{product2.beta}</span>
          </div>
        </div>
      </div>

      {/* 风险收益象限图 */}
      <div className="relative w-full overflow-hidden rounded-lg border border-gray-200 p-3">
        <h3 className="text-left text-base font-semibold text-gray-900">风险收益象限图</h3>
        {/* 象限区域 */}
        <div className="mb-2 flex">
          {/* 左上：高收益低风险 */}
          <div className="relative mr-4 h-[89px] w-1/2 rounded-lg bg-blue-50">
            <div className="absolute left-1/2 top-8 -translate-x-1/2 transform">
              <span className="text-xs font-semibold text-blue-600">高收益低风险</span>
            </div>
            <div className="absolute bottom-0 z-[111] translate-y-[20px] rounded bg-white px-1 py-1 shadow-md">
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-900">收益</span>
                <ArrowUp size={12} />
              </div>
            </div>
          </div>

          {/* 右上：高收益高风险 */}
          <div className="relative h-[89px] w-1/2 rounded-lg bg-red-50">
            <div className="absolute left-4 top-4 flex items-center gap-1">
              <div className="h-2 w-2 rounded-full bg-blue-600"></div>
              <span className="text-xs text-gray-900">新能源</span>
            </div>
            <div className="absolute left-1/2 top-8 -translate-x-1/2 transform">
              <span className="text-xs font-semibold text-red-600">高收益高风险</span>
            </div>
            <div className="absolute bottom-2 left-4 flex items-center gap-1">
              <div className="h-2 w-2 rounded-full bg-orange-600"></div>
              <span className="text-xs text-gray-900">消费</span>
            </div>
          </div>
        </div>
        {/* 坐标轴 */}
        <div className="absolute left-1/2 top-[17%] h-[calc(100%-70px)] h-full w-px -translate-x-[2px] transform bg-gray-300"></div>
        <div className="h-px translate-y-[-4px] bg-gray-300"></div>

        <div className="flex">
          {/* 左下：低收益低风险 */}
          <div className="relative mr-4 flex h-[44px] w-1/2 items-center justify-center rounded-lg bg-indigo-50">
            <span className="text-xs font-semibold text-indigo-600">低收益低风险</span>
          </div>
          {/* 右下：低收益高风险 */}
          <div className="relative flex h-[44px] w-1/2 items-center justify-center rounded-lg bg-orange-50">
            <span className="text-xs font-semibold text-orange-600">低收益高风险</span>
          </div>
        </div>

        {/* 坐标轴标签 */}

        <div>
          <div className="m-auto w-full">
            <div className="relative z-[111] inline-flex translate-y-[-20px] items-center gap-1 rounded bg-white p-1 shadow-md">
              <span className="text-xs text-gray-900">风险</span>
              <ArrowUp size={12} />
            </div>
          </div>
        </div>
      </div>

      {/* AI总结部分 */}
      <Summary
        title="AI总结-风险收益"
        content={totalData?.风险评估对比}
        isShowSuggestions={false}
      />
    </div>
  );
};

export default RiskAssessmentComparison;
